import { Button } from '../ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { ArrowRight, HelpCircle, CheckCircle2 } from 'lucide-react';
import { WA_URL } from '../../lib/wa';

const Item1 = () => (
  <AccordionContent className="px-8 pb-6 text-gray-600 leading-relaxed">
    <p className="mb-4">
      <strong className="text-gray-900">Yes — Atlas works on an exclusive basis.</strong> We take on a limited number of brand slots per category, and operate one brand per category, so every partner gets undivided attention, dedicated shelf space, and no cannibalisation from competing products in our network.
    </p>
    <p>
      This focused approach is what allows us to commit real distribution muscle, marketing spend, and consumer intelligence to your launch. Because slots are limited, we evaluate each category carefully and onboard the brand we believe can win it.
    </p>
  </AccordionContent>
);

const Item2 = () => (
  <AccordionContent className="px-8 pb-6 text-gray-600 leading-relaxed">
    <p className="mb-4">
      We typically work on a <strong className="text-gray-900">purchase order basis</strong>—we buy your inventory upfront and take on the distribution responsibility. This means <strong className="text-[#004aad]">faster payments and lower risk for you</strong>.
    </p>
    <p className="mb-4">
      For premium or high-value products, we're open to discussing <strong className="text-gray-900">consignment arrangements</strong> where appropriate.
    </p>
    <p>Either way, you get paid fairly and on time. No 90-day wait cycles or hidden deductions.</p>
  </AccordionContent>
);

const Item3 = () => (
  <AccordionContent className="px-8 pb-6 text-gray-600 leading-relaxed">
    <p className="mb-4">Pricing is <strong className="text-gray-900">collaborative and data-driven</strong>. We analyze:</p>
    <ul className="space-y-2 mb-4 ml-6">
      {[
        'Your cost structure and desired margins',
        'Competitor pricing in ANZ market',
        'Consumer willingness to pay (from our market intelligence)',
        'Import duties, logistics, and operational costs',
      ].map((b) => (
        <li key={b} className="flex items-start">
          <CheckCircle2 className="h-5 w-5 text-[#004aad] mr-2 flex-shrink-0 mt-0.5" />
          <span>{b}</span>
        </li>
      ))}
    </ul>
    <p>We recommend an initial price point, test it in market, and <strong className="text-[#004aad]">optimize based on real sales data</strong>. You stay profitable, and consumers see value.</p>
  </AccordionContent>
);

const Item4 = () => (
  <AccordionContent className="px-8 pb-6 text-gray-600 leading-relaxed">
    <p className="mb-4">
      From initial discussion to products being live on our app and in select retail stores: <strong className="text-[#004aad]">typically 3-4 weeks</strong>.
    </p>
    <p className="mb-4"><strong className="text-gray-900">Timeline breakdown:</strong></p>
    <ul className="space-y-2 mb-4 ml-6">
      {[
        ['Week 1:', 'Onboarding, product assessment, compliance check'],
        ['Week 2:', 'Logistics coordination, inventory arrival'],
        ['Week 3:', 'App listing setup, retail partner coordination'],
        ['Week 4:', 'Soft launch, first sales, feedback loop begins'],
      ].map(([w, t]) => (
        <li key={w} className="flex items-start">
          <span className="text-[#004aad] font-bold mr-3">{w}</span>
          <span>{t}</span>
        </li>
      ))}
    </ul>
    <p>Of course, complex products or regulatory requirements may take longer—but we move fast without cutting corners.</p>
  </AccordionContent>
);

const Item5 = () => {
  const cards = [
    ['Dedicated Account Manager', 'Your single point of contact for everything'],
    ['Weekly Performance Reports', 'Sales data, insights, and recommendations'],
    ['Marketing & Social Support', 'Content creation, campaigns, and visibility'],
    ['24/7 Access to Team', "Questions, concerns, or opportunities—we're available"],
  ];
  return (
    <AccordionContent className="px-8 pb-6 text-gray-600 leading-relaxed">
      <p className="mb-4"><strong className="text-gray-900">You're not just getting a distributor—you're getting a dedicated team.</strong></p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {cards.map(([title, body]) => (
          <div key={title} className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-bold text-gray-900 mb-2 flex items-center">
              <CheckCircle2 className="h-5 w-5 text-[#004aad] mr-2" />
              {title}
            </h4>
            <p className="text-sm">{body}</p>
          </div>
        ))}
      </div>
      <p>Plus, our local teams in Sydney, Auckland, and Noida ensure <strong className="text-[#004aad]">you always have boots on the ground</strong> working for your brand.</p>
    </AccordionContent>
  );
};

const FAQS = [
  { q: 'Do you take exclusivity?', Body: Item1 },
  { q: 'Do you buy inventory or work on consignment?', Body: Item2 },
  { q: 'How is pricing decided?', Body: Item3 },
  { q: 'How fast can we launch?', Body: Item4 },
  { q: 'What support do we get?', Body: Item5 },
];

export const Faq = () => (
  <section className="py-20 md:py-32 bg-gray-50">
    <div className="container mx-auto px-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-blue-50 text-[#004aad] px-5 py-2 rounded-full text-sm font-semibold mb-6">
            <HelpCircle className="h-4 w-4" />
            <span>COMMON QUESTIONS</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <p className="text-xl text-gray-600">Clear answers to help you make an informed decision about partnering with Atlas.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i + 1}`} className="border-b border-gray-100 last:border-0">
                <AccordionTrigger className="px-8 py-6 text-lg font-semibold text-gray-900 hover:no-underline hover:text-[#004aad] transition-colors">
                  {f.q}
                </AccordionTrigger>
                <f.Body />
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="mt-12 text-center bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <p className="text-lg text-gray-700 mb-6">
            <strong className="text-[#004aad]">Still have questions?</strong> We're here to help. Every brand is unique, and we're happy to discuss your specific situation.
          </p>
          <Button asChild size="lg" className="bg-[#004aad] hover:bg-[#003a8c] text-white text-lg px-10 py-6 transition-all duration-300 hover:shadow-lg" data-testid="faq-lets-talk-btn">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Let's Talk
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </Button>
        </div>
      </div>
    </div>
  </section>
);
